import { Link } from "react-router-dom";
import { useState } from "react";
import { Mail, Phone, MapPin, ArrowUpRight, Linkedin, Twitter, Youtube, Facebook, Instagram } from "lucide-react";
import Logo from "./Logo";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await subscribeNewsletter(email);
      toast.success("Subscribed. We'll keep you posted.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Try again.");
    } finally { setLoading(false); }
  };

  return (
    <footer className="relative bg-[#0F172A] text-slate-300 overflow-hidden" data-testid="site-footer">
      <div className="absolute inset-0 bg-grid-dark opacity-50 pointer-events-none" />
      <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[color:var(--brand)] blur-[160px] opacity-25 pointer-events-none" />

      <div className="relative container-x pt-20 pb-10">
        {/* CTA Strip */}
        <div className="grid md:grid-cols-2 gap-10 items-center pb-16 border-b border-white/10">
          <div>
            <div className="eyebrow text-cyan-400">Ready to transform?</div>
            <h3 className="text-3xl md:text-5xl font-bold text-white mt-3 leading-[1.05]">
              Bring your clinic, hospital or lab <span className="text-[color:var(--brand)]">online in 48 hours*</span>
            </h3>
          </div>
          <div className="flex flex-col gap-4 md:items-end">
            {/* <p className="text-slate-400 md:text-right max-w-md">
              Book a personalised walkthrough of the SAALUVARS ecosystem — no commitments, real numbers, real workflows.</p> */}
            <div className="flex gap-4">
              {/*
              <Link to="/request-demo" className="btn-primary" data-testid="footer-request-demo">
                Request Demo <ArrowUpRight className="w-4 h-4" />
              </Link>
              <Link to="/contact" className="btn-ghost !bg-transparent !text-white !border-white/30 hover:!border-white">
                Talk to Sales
              </Link>
              */}
            </div>
          </div>
        </div>

        {/* Columns */}
        <div className="grid md:grid-cols-12 gap-10 py-14">
          <div className="md:col-span-4">
            <Logo size="lg" />
            <p className="mt-5 text-slate-400 max-w-sm">
              SAALUVARS is the AI-powered unified healthcare super-platform connecting hospitals, clinics, doctors, pharmacies, labs and patients into one seamless ecosystem.
            </p>
            <form onSubmit={onSubscribe} className="mt-6 flex gap-2 max-w-sm" data-testid="newsletter-form">
              {/* <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your work email"
                className="flex-1 bg-white/5 border border-white/15 rounded-full px-4 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-white/40"
                data-testid="newsletter-input"
              /> */}
              {/* <button
                type="submit"
                disabled={loading}
                className="btn-primary py-2.5 px-5 text-sm"
                data-testid="newsletter-submit"
              >
                {loading ? "..." : "Subscribe"}
              </button> */}
            </form>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Platform</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/solutions" className="hover:text-white">Solutions</Link></li>
              <li><Link to="/apps" className="hover:text-white">Apps</Link></li>
              <li><Link to="/ai-suite" className="hover:text-white">AI Suite</Link></li>
              <li><Link to="/pricing" className="hover:text-white">Pricing</Link></li>
              <li><Link to="/integrations" className="hover:text-white">Integrations</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Company</div>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/about" className="hover:text-white">About</Link></li>
              <li><Link to="/industries" className="hover:text-white">Industries</Link></li>
              <li><Link to="/security" className="hover:text-white">Security</Link></li>
              {/*<li><Link to="/contact" className="hover:text-white">Contact</Link></li>*/}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="text-xs uppercase tracking-[0.18em] text-slate-500 font-semibold mb-4">Reach Us</div>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3"><Mail className="w-4 h-4 mt-0.5 text-[color:var(--brand)]" /><a href="mailto:info@saaluvars.com" className="hover:text-white">info@saaluvars.com</a></li>
              <li className="flex items-start gap-3"><Phone className="w-4 h-4 mt-0.5 text-[color:var(--brand)]" /><a href="tel:+919600043880" className="hover:text-white">+91 9600043880</a></li>
              <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 text-[color:var(--brand)]" /><span>Chennai</span></li>
            </ul> 
            <div className="mt-6 flex gap-3">
              {[
                { Icon: Linkedin, href: "https://www.linkedin.com/showcase/saaluvars", label: "LinkedIn" },
                { Icon: Instagram, href: "https://www.instagram.com/saaluvars/", label: "Instagram" },
                { Icon: Facebook, href: "https://www.facebook.com/people/Saaluvars/61592920144243/", label: "Facebook" }
              ].map(({ Icon, href, label }, i) => (
                <a key={i} href={href} target="_blank" rel="noopener noreferrer" className="w-9 h-9 grid place-items-center rounded-full border border-white/15 hover:border-white/50 hover:text-white transition" aria-label={label}>
                  <Icon className="w-4 h-4" />
                </a>
              ))} 
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pt-6 border-t border-white/10 text-xs text-slate-500">
          <div>© {new Date().getFullYear()} SAALUVARS Medical Tech. Connecting Healthcare. Empowering Lives.</div>
          <div className="flex gap-5">
            <Link to="/privacy-policy" className="hover:text-white">Privacy</Link>
            <Link to="/terms" className="hover:text-white">Terms</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
